package org.zest.insan_360_website.components.widgets

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.FaCaretDown
import com.varabyte.kobweb.silk.components.icons.fa.FaCaretUp
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.ExperimentalComposeWebApi
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.zest.insan_360_website.components.layouts.assessmentWordLibrary
import org.zest.insan_360_website.data.localized
import org.zest.insan_360_website.pages.Answer
import org.zest.insan_360_website.pages.Question

enum class StatementCellState {
    MORE, LESS, UNSELECTED
}

val QuestionCellStyle = CssStyle {
    base {
        Modifier
            .fontSize(1.cssRem)
            .alignItems(AlignItems.Center)
    }

    Breakpoint.LG {
        Modifier
            .fontSize(1.1.cssRem)
            .alignItems(AlignItems.Center)
    }

    Breakpoint.XL {
        Modifier
            .alignItems(AlignItems.Start)
    }
}

val QuestionCellGridStyle = CssStyle {
    base {
        Modifier.grid {
            rows { repeat(4) { size(1.fr) } }
        }
    }
    Breakpoint.XL {
        Modifier.grid {
            rows { repeat(2) { size(1.fr) } }
            columns { repeat(2) { size(1.fr) } }
        }
    }
}


val moreOrLessButtonStyle = CssStyle {
    base {
        Modifier
            .height(50.percent)
            .width(100.percent)
            .borderRadius(0.5.cssRem)
            .transition {
                property("scale")
                duration(0.25.s)
            }
            .cursor(Cursor.Pointer)
    }
}

@Composable
fun QuestionCell(
    number: Int,
    question: Question,
    savedAnswer: Answer?,
    onAnswerChanged: (Answer) -> Unit
) {
    var expanded by remember { mutableStateOf("") }
    var more by remember(question) { mutableStateOf(savedAnswer?.moreStatement) }
    var less by remember(question) { mutableStateOf(savedAnswer?.lessStatement) }

    Column(
        modifier = Modifier
            .display(DisplayStyle.Flex)
            .then(QuestionCellStyle.toModifier()),
    ) {
        SpanText(
            modifier = Modifier
                .fontSize(1.75.cssRem)
                .padding(bottom = 1.cssRem)
                .fontWeight(FontWeight.Bold),
            text = (assessmentWordLibrary.question.localized) + " $number"
        )
        Div(
            attrs = Modifier
                .border(
                    width = 0.25.cssRem,
                    color = Colors.Black,
                    style = LineStyle.Solid
                )
                .borderRadius(1.cssRem)
                .gap(0.5.cssRem)
                .padding(0.5.cssRem)
                .then(QuestionCellGridStyle.toModifier())
                .toAttrs()
        ) {
            question.statements.forEach { statement ->
                StatementCell(
                    statement.text,
                    expanded,
                    more?.text ?: "",
                    less?.text ?: "",
                    { expanded = it },
                    { state ->
                        when (state) {

                            StatementCellState.MORE -> {
                                if (less == statement) less = null
                                more = statement
                            }

                            StatementCellState.LESS -> {
                                if (more == statement) more = null
                                less = statement
                            }

                            else -> {}
                        }
                        onAnswerChanged(Answer(more, less))
                    }
                )
            }
        }
    }
}

@OptIn(ExperimentalComposeWebApi::class)
@Composable
fun StatementCell(
    text: String,
    toExpand: String,
    more: String,
    less: String,
    onMouseEnter: (String) -> Unit,
    onSelected: (StatementCellState) -> Unit
) {
    var cellState = when (text) {
        more -> StatementCellState.MORE
        less -> StatementCellState.LESS
        else -> StatementCellState.UNSELECTED
    }

    Div(
        attrs = Modifier
            .width(16.cssRem)
            .height(8.cssRem)
            .borderRadius(0.5.cssRem)
            .onMouseEnter {
                onMouseEnter(text)
            }
            .onMouseLeave {
                onMouseEnter("")
            }
            .toAttrs(),
    ) {
        Row(
            modifier = Modifier.fillMaxSize()
        ) {
            Box(
                modifier = Modifier
                    .height(100.percent)
                    .width(
                        if (toExpand == text) 75.percent else 100.percent
                    )
                    .padding(0.75.cssRem)
                    .borderRadius(0.5.cssRem)
                    .backgroundColor(
                        when (cellState) {
                            StatementCellState.MORE -> Colors.SkyBlue
                            StatementCellState.LESS -> Colors.CadetBlue
                            else -> Colors.LightGray
                        }
                    )
                    .styleModifier {
                        transitions {
                            "width" { duration(0.25.s) }
                            "background-color" { duration(0.25.s) }
                        }
                    },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    modifier = Modifier
                        .textAlign(TextAlign.Center)
                        .color(
                            when (cellState) {
                                StatementCellState.MORE -> Colors.Black
                                StatementCellState.LESS -> Colors.White
                                else -> Colors.Black
                            }
                        ),
                    text = text
                )
            }
            Column(
                modifier = Modifier
                    .width(
                        if (toExpand == text) 25.percent else 0.percent
                    )
                    .height(100.percent)
                    .padding(left = if (toExpand == text) 0.25.cssRem else 0.px)
                    .gap(0.125.cssRem)
                    .transition {
                        property("width")
                        duration(0.25.s)
                    }
            ) {
                MoreOrLessButton(
                    isExpand = toExpand == text,
                    color = Colors.SkyBlue,
                    onClick = {
                        cellState = StatementCellState.MORE
                        onSelected(cellState)
                    },
                    content = {
                        FaCaretUp(
                            modifier = Modifier
                                .color(Colors.White)
                                .scale(1.5)
                        )
                        SpanText(
                            modifier = Modifier
                                .fontSize(0.75.cssRem)
                                .color(Colors.White),
                            text = assessmentWordLibrary.more.localized,
                        )
                    }
                )
                MoreOrLessButton(
                    isExpand = toExpand == text,
                    color = Colors.CadetBlue,
                    onClick = {
                        cellState = StatementCellState.LESS
                        onSelected(cellState)
                    },
                    content = {
                        SpanText(
                            modifier = Modifier
                                .fontSize(0.75.cssRem)
                                .color(Colors.White),
                            text = assessmentWordLibrary.less.localized,
                        )
                        FaCaretDown(
                            modifier = Modifier
                                .color(Colors.White)
                                .scale(1.5)
                        )
                    }
                )
            }
        }
    }
}

@Composable
fun MoreOrLessButton(isExpand: Boolean, color: CSSColorValue, onClick: () -> Unit, content: @Composable () -> Unit) {
    Column(
        modifier = Modifier
            .scale(if (isExpand) 1.0 else 0)
            .backgroundColor(color)
            .onClick {
                onClick()
            }
            .then(moreOrLessButtonStyle.toModifier()),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        content()
    }
}